import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  AccessTime,
  VolunteerActivism,
  Warning,
  Info,
  CheckCircle,
  ShoppingCart,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import ParcelDayInfo from '../components/ParcelDayInfo';
import ContactTiles from '../components/shared/ContactTiles';
import { parcelDaySelectionApi } from '../services/parcel-day-selection';
import { api } from '../services/api';

interface FoodParcel {
  id: number;
  name: string;
  description: string;
  price: string | number;
  image: string;
}

const FoodParcelsPage: React.FC = () => {
  const navigate = useNavigate();
  const [parcels, setParcels] = useState<FoodParcel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canPlaceOrders, setCanPlaceOrders] = useState(false);
  const [parcelDayData, setParcelDayData] = useState<Awaited<ReturnType<typeof parcelDaySelectionApi.getAvailableForOrder>> | null>(null);

  useEffect(() => {
    const fetchPageData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch food parcels and order times in parallel for faster load
        const startTime = Date.now();
        const [parcelsResponse, orderTimesData] = await Promise.all([
          api.get('/v1/food-parcels'),
          parcelDaySelectionApi.getAvailableForOrder(),
        ]);
        const endTime = Date.now();
        console.log('🔍 [DEBUG] Page data loaded in', endTime - startTime, 'ms');

        const parcelsData = parcelsResponse.data.data || [];
        const parcelsWithNumericPrices = parcelsData.map((parcel: FoodParcel) => ({
          ...parcel,
          price: typeof parcel.price === 'string' ? parseFloat(parcel.price) : parcel.price,
        }));
        setParcels(parcelsWithNumericPrices);
        setCanPlaceOrders(orderTimesData.can_place_orders);
        setParcelDayData(orderTimesData);
      } catch (err) {
        console.error('🔍 [DEBUG] Error in fetchPageData:', err);
        console.error('🔍 [DEBUG] Error type:', typeof err);
        console.error('🔍 [DEBUG] Error constructor:', err?.constructor?.name);
        if (err instanceof Error) {
          console.error('🔍 [DEBUG] Error message:', err.message);
          console.error('🔍 [DEBUG] Error stack:', err.stack);
        }
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  const handleSelectParcel = (parcel: FoodParcel) => {
    // Navigate directly to order page when a parcel is selected
    navigate(`/order`, { 
      state: { 
        foodParcel: parcel,
        autoAssignParcelDay: true
      } 
    });
  };

  const handleParcelDaySelected = () => {
    // Handle parcel day selection
  };

  // Remove selectedParcel state as it's no longer needed

  const handleNavigateToVolunteer = () => {
    navigate('/volunteer');
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <CircularProgress />
        <Typography color="text.secondary">Verifying the order times...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome notice - new website (full width, prominent like volunteer card) */}
      <Box
        sx={{
          width: '100%',
          p: 4,
          mb: 4,
          bgcolor: 'info.50',
          border: '2px solid',
          borderColor: 'info.200',
          borderRadius: 2,
          fontSize: '1.15rem',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Info sx={{ fontSize: 48, color: 'info.600', flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1.5, color: 'info.800' }}>
              Welcome to our new website
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: 'info.700', mb: 0 }}>
              Like all things new there may be little issues from time to time. If you experience any issues please let us know at{' '}
              <a href="mailto:orders@newlifefoodbarn.org" style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 'bold' }}>orders@newlifefoodbarn.org</a>. We will try to address it as soon as possible.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Food Parcels
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Affordable, quality food parcels for our Gold Coast community
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          The $45 parcel with the optional fruit & veggie box and groceries roughly feeds a young family of 4-5 for a week 
          and contains around $200-$250 worth of food, filling the boot of a small car.
        </Typography>
      </Box>

      {/* Top Notices - Side by Side */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Box 
            sx={{ 
              p: 3,
              height: '100%',
              bgcolor: 'warning.50',
              border: '2px solid',
              borderColor: 'warning.200',
              borderRadius: 2,
              fontSize: '1.1rem'
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <VolunteerActivism sx={{ fontSize: 36, color: 'warning.600' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'warning.800' }}>
                  🚨 WE URGENTLY NEED VOLUNTEERS!
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1rem', color: 'warning.700' }}>
                  Any age welcome. Do you need to get out and feel great about yourself? 
                  Come and help the team making a difference in thousands of lives.
                </Typography>
                <Typography variant="body2" paragraph sx={{ fontSize: '0.95rem', color: 'warning.700' }}>
                  We are a registered workplace for Community Services, Centrelink & SPER.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleNavigateToVolunteer}
                  sx={{ 
                    mt: 1,
                    bgcolor: 'warning.500',
                    '&:hover': { bgcolor: 'warning.600' }
                  }}
                >
                  Sign Up as a Volunteer Today!
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box 
            sx={{ 
              p: 3,
              height: '100%',
              bgcolor: 'info.50',
              border: '2px solid',
              borderColor: 'info.200',
              borderRadius: 2,
              fontSize: '1.1rem'
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <AccessTime sx={{ fontSize: 36, color: 'info.600' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: 'info.800' }}>
                  ⏰ PLEASE NOTE CHANGES IN ORDER CUT-OFF TIMES
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1rem', color: 'info.700' }}>
                  The cut-off time for online orders are <strong>2:30pm</strong> on Tuesday and Wednesday evenings, 
                  <strong> UNLESS SOLD OUT PRIOR</strong>.
                </Typography>
                <Typography variant="body2" paragraph sx={{ fontSize: '0.95rem', color: 'info.700' }}>
                  We recommend placing orders early to ensure that you don't miss out as orders close at a certain amount, 
                  or at 2:30pm, whichever comes first.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.95rem', color: 'info.700' }}>
                  If you're unable to place your order online please call <strong>0410 565 781</strong> before 2:30pm 
                  the day before the parcel is required.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Food Parcel Day Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ mb: 2 }}>
          📅 Available Food Parcel Days
        </Typography>
        <ParcelDayInfo
          onParcelDaySelected={handleParcelDaySelected}
          initialData={parcelDayData}
        />
      </Box>

      {/* Order Status Alert */}
      {!canPlaceOrders && (
        <Alert severity="warning" sx={{ mb: 3, fontSize: '0.875rem', py: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            🚫 Orders Currently Closed
          </Typography>
          <Typography variant="body2">
            No orders can be placed at this time. Please check the schedule above for when ordering will be available.
          </Typography>
        </Alert>
      )}

      {/* Pricing Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
          💰 2026 Pricing
        </Typography>
        <Card sx={{ p: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <strong>Prices for 2026 remain mostly the same as 2025:</strong>
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>The Standard parcel starts at $45</strong> + optional Fruit & Veg Box $15.00 + optional Grocery Package $12.00 = <strong>$72.00</strong>.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Delivery is extra:</strong> Starts at $20 from Arundel to Nerang Broadbeach Road to Nerang. 
              Further south is $25 – Markeri Street out to Merrimac and so on.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Please see the <a href="/delivery-fees" style={{ color: 'inherit', textDecoration: 'underline' }}>Delivery Fees Listing for your area</a>
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>

      {/* Parcel Contents */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
          📦 What's Included
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  Each parcel includes:
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="A selection of meats" 
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Chiller pack" 
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="A selection of bread" 
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Discounted grocery items" 
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>
                  Optional extras can be added:
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ShoppingCart color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Fruit & Veg Box – $15.00" 
                      secondary="Fresh produce selection"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.8rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ShoppingCart color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="2nd Fruit & Veggie box – $26" 
                      secondary="Additional produce"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.8rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ShoppingCart color="primary" sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Grocery Package – $12.00" 
                      secondary="25 pre-selected items"
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.8rem' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Food Parcels Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4, 
          p: 3, 
          bgcolor: 'primary.light', 
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.main'
        }}>
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold', 
            color: 'primary.contrastText',
            mb: 1
          }}>
            🛒 Select Your Food Parcel
          </Typography>
          <Typography variant="h6" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>
            Choose from our available food parcel options
          </Typography>
        </Box>
        
        <Grid container spacing={3} justifyContent="center">
          {parcels.map((parcel) => (
            <Grid item xs={12} sm={6} md={4} key={parcel.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  border: '2px solid',
                  borderColor: canPlaceOrders ? 'primary.main' : 'grey.300',
                  boxShadow: canPlaceOrders ? 3 : 1,
                  opacity: canPlaceOrders ? 1 : 0.6,
                  '&:hover': {
                    transform: canPlaceOrders ? 'translateY(-4px)' : 'none',
                    boxShadow: canPlaceOrders ? 6 : 1,
                    cursor: canPlaceOrders ? 'pointer' : 'default',
                  },
                }}
                onClick={canPlaceOrders ? () => handleSelectParcel(parcel) : undefined}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography gutterBottom variant="h5" component="h3" sx={{ 
                      fontWeight: 'bold',
                      minHeight: '2.5rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {parcel.name}
                    </Typography>
                    <Typography paragraph sx={{ 
                      mb: 2, 
                      minHeight: '4rem',
                      display: 'flex',
                      alignItems: 'flex-start'
                    }}>
                      {parcel.description}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="h4" color="primary" sx={{ 
                      fontWeight: 'bold',
                      textAlign: 'center',
                      mb: 2
                    }}>
                      ${Number(parcel.price).toFixed(2)}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      sx={{ 
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold'
                      }}
                      disabled={!canPlaceOrders}
                    >
                      {canPlaceOrders ? '🛒 Order This Parcel' : '🚫 ORDERS CLOSED'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Food parcel selection now directly navigates to order page */}
      </Box>

      {/* Important Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 3 }}>
          ℹ️ Important Information
        </Typography>
        
        <Accordion>
          <AccordionSummary expandIcon={<Info />}>
            <Typography variant="h6">Payment Methods</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              <strong>We now only accept online Card payments, or Direct Bank Deposits.</strong>
            </Typography>
            <Typography variant="body1" paragraph>
              Payment by Cash or Eftpos at pickup is only by prior arrangement.
            </Typography>
            <Typography variant="body1">
              Vouchers can be added on the top of the Checkout screen – please type in the total value of the voucher.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<Info />}>
            <Typography variant="h6">Delivery Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We can deliver parcels when you are not able to pick up. Delivery will be between 8am to 2:30pm, 
              based on the amount of deliveries for different areas on the day.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> You must have your phone on from 7:00am for delivery drivers 
                otherwise your parcel will be cancelled and no further parcels unless a $20 repacking fee is paid.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<Warning />}>
            <Typography variant="h6">Product Condition & Storage Guidelines</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Info color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="A lot of our products are 'seconds'"
                  secondary="Some items may be close to or past their 'best before' date but are generally OK for some time."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Info color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Products marked beyond 'use by' date"
                  secondary="Have been frozen beforehand."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Info color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Packaging may be damaged"
                  secondary="But the goods within the package are still good."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Info color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Meat products"
                  secondary="Most come straight from the butcher and are frozen. Some are pre-cooked and require reheating to 75°C."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Info color="warning" />
                </ListItemIcon>
                <ListItemText 
                  primary="Vegetables"
                  secondary="May be seconds and ripe, requiring attention and refrigeration."
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<Info />}>
            <Typography variant="h6">Special Offers & Membership</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              <strong>Deals:</strong> There is potentially free fruit, veg and other items for pickup clients pending availability.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Referral Discount:</strong> If you personally introduce a friend you receive 5% off your next parcel.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Special Dietary Needs:</strong> We occasionally have stock that caters for specialty diets. 
              Check our available items when ordering.
            </Typography>
            <Typography variant="body1">
              <strong>Membership:</strong> Not required for parcel orders, but required for entering the Foodbarn Shop on Thursdays. 
              Membership is free and can be completed on arrival.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Contact Information */}
      <ContactTiles />

      {/* Reminder */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body1">
          <strong>Please bring your bags and chiller bags</strong> to pack your shopping and for safe food transport and keeping.
        </Typography>
      </Alert>
    </Container>
  );
};

export default FoodParcelsPage; 